import {useCallback, useEffect, useMemo, useState} from 'react'
import {Heading} from '@/components/tailwind/heading'
import {Driver} from '@/models/driver'
import {getDrivers, updateDriverCurrentNotes} from '@/services/driver-services'
import {useAuth} from '@/context/use-auth'
import {useReportsTranslation, useCommonTranslation} from '@/i18n/hooks'
import {Text} from '@/components/tailwind/text'
import {Input, InputGroup} from '@/components/tailwind/input'
import {MagnifyingGlassIcon} from '@heroicons/react/16/solid'
import {DriverDataTable} from './driver-data-table'
import {createDriverColumns} from './driver-columns'
import {Button} from '@/components/tailwind/button'
import {FileSpreadsheet} from 'lucide-react'
import {useToast} from '@/hooks/use-toast'
import log from '@/lib/logger'

function DriverListPage() {
    const [drivers, setDrivers] = useState<Driver[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const {token} = useAuth()
    const {t} = useReportsTranslation()
    const {t: tCommon} = useCommonTranslation()
    const {toast} = useToast()

    useEffect(() => {
        async function fetchDrivers() {
            if (!token) {
                log.error('No token available for fetching drivers')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                const data = await getDrivers(token)
                setDrivers(data)
            } catch (err) {
                log.error('Error fetching drivers:', err)
                setError(t('drivers.error'))
            } finally {
                setLoading(false)
            }
        }

        fetchDrivers()
    }, [token])

    const handleCurrentNotesUpdate = useCallback(async (driverId: string, currentNotes: string | null) => {
        if (!token) return

        await updateDriverCurrentNotes(token, driverId, currentNotes)

        setDrivers(prev => prev.map(driver =>
            driver.id === driverId
                ? {...driver, currentNotes: currentNotes ?? undefined}
                : driver
        ))
    }, [token])

    const columns = useMemo(
        () => createDriverColumns({onCurrentNotesUpdate: handleCurrentNotesUpdate}),
        [handleCurrentNotesUpdate]
    )

    async function handleExportExcel() {
        if (!token) return

        try {
            const apiUrl = import.meta.env.VITE_NBH_URL_ROOT || import.meta.env.VITE_API_URL || 'http://localhost:8080'

            const response = await fetch(
                `${apiUrl}/api/drivers/export/excel`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            )

            if (!response.ok) {
                throw new Error('Excel download failed')
            }

            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')

            // Extract filename from Content-Disposition header
            const contentDisposition = response.headers.get('Content-Disposition')
            let filename = 'Fahrerliste.xlsx' // fallback

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1]
                }
            }

            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (err) {
            log.error('Error downloading Excel:', err)
            toast({
                title: t('drivers.export.error'),
                description: t('drivers.export.errorDescription'),
                variant: 'destructive'
            })
        }
    }

    if (loading) {
        return (
            <div className="container mx-auto py-6">
                <Text>{tCommon('loading')}</Text>
            </div>
        )
    }

    if (error) {
        return (
            <div className="container mx-auto py-6">
                <Text className="text-red-500">{error}</Text>
            </div>
        )
    }

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-start justify-between">
                <div>
                    <Heading>{t('drivers.title')}</Heading>
                    <Text>{t('drivers.description')}</Text>
                </div>
                <Button
                    plain
                    onClick={handleExportExcel}
                    className="flex items-center gap-2"
                    title={t('drivers.export.excel')}
                >
                    <FileSpreadsheet className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('drivers.export.excel')}</span>
                </Button>
            </div>

            <div>
                <InputGroup>
                    <MagnifyingGlassIcon/>
                    <Input
                        type="text"
                        placeholder={t('drivers.search.placeholder')}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        aria-label={t('drivers.search.ariaLabel')}
                    />
                </InputGroup>
            </div>

            {drivers.length === 0 ? (
                <Text className="italic text-gray-500">{t('drivers.noDrivers')}</Text>
            ) : (
                <DriverDataTable
                    columns={columns}
                    data={drivers}
                    searchTerm={searchTerm}
                />
            )}
        </div>
    )
}

export default DriverListPage
